import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const PropertyForm = ({ onPropertyAdded, propertyToEdit, onCancel }) => {
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [sqft, setSqft] = useState('');
  const [tag, setTag] = useState('For Sale');
  
  // New state for multiple images
  const [images, setImages] = useState([]); // Array of { file: File | null, url: string, type: 'file' | 'url' }
  const [currentUrl, setCurrentUrl] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (propertyToEdit) {
      setTitle(propertyToEdit.title || '');
      setLocation(propertyToEdit.location || '');
      setPrice(propertyToEdit.price || '');
      setDescription(propertyToEdit.description || '');
      setBeds(propertyToEdit.beds || '');
      setBaths(propertyToEdit.baths || '');
      setSqft(propertyToEdit.sqft || '');
      setTag(propertyToEdit.tag || 'For Sale');
      
      if (propertyToEdit.images && propertyToEdit.images.length > 0) {
        setImages(propertyToEdit.images.map(url => ({ url, type: 'url', file: null })));
      } else if (propertyToEdit.image) {
        setImages([{ url: propertyToEdit.image, type: 'url', file: null }]);
      } else {
        setImages([]);
      }
    }
  }, [propertyToEdit]);

  const handleAddImageFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newImages = Array.from(e.target.files).map(file => ({
        file: file,
        url: URL.createObjectURL(file), // Preview URL
        type: 'file'
      }));
      setImages(prev => [...prev, ...newImages]);
    }
  };

  const handleAddImageUrl = () => {
    if (currentUrl.trim()) {
      setImages(prev => [...prev, { file: null, url: currentUrl, type: 'url' }]);
      setCurrentUrl('');
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (images.length === 0) {
        throw new Error("Please provide at least one image (URL or upload).");
      }

      // Process images: upload files and collect URLs
      const processedImageUrls = await Promise.all(images.map(async (img) => {
        if (img.type === 'file') {
          const imageRef = ref(storage, `property_images/${img.file.name + Date.now()}`);
          await uploadBytes(imageRef, img.file);
          return await getDownloadURL(imageRef);
        } else {
          return img.url;
        }
      }));

      // Use the first image as the primary thumbnail
      const primaryImage = processedImageUrls[0];

      const propertyData = {
        title,
        location,
        price,
        description,
        beds,
        baths,
        sqft,
        tag,
        image: primaryImage, // Primary thumbnail
        images: processedImageUrls, // All images
        updatedAt: new Date()
      };

      if (propertyToEdit) {
        await updateDoc(doc(db, 'properties', propertyToEdit.id), propertyData);
        alert('Property updated successfully!');
      } else {
        propertyData.createdAt = new Date();
        await addDoc(collection(db, 'properties'), propertyData);
        alert('Property added successfully!');
      }

      // Reset form
      setTitle('');
      setLocation('');
      setPrice('');
      setDescription('');
      setBeds('');
      setBaths('');
      setSqft('');
      setTag('For Sale');
      setImages([]);
      
      if (onPropertyAdded) onPropertyAdded();

    } catch (err) {
      console.error("Error saving document: ", err);
      setError('Error saving property: ' + err.message);
    }
    setLoading(false);
  };

  return (
    <div className="property-form-card" style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '2rem' }}>
      <h3 style={{ marginBottom: '1.5rem', color: 'var(--color-primary)' }}>{propertyToEdit ? 'Edit Property' : 'Add New Property'}</h3>
      {error && <div className="alert alert-danger" style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
      <form onSubmit={handleSubmit}>
        {/* ... (fields remain the same, validation is handled via state population) ... */}
        
        {/* ... (rest of the form fields block in the original file, I am not replacing them here, just the wrapper logic but replace_file_content needs contiguous block. 
           Actually, the header change is at top, buttons at bottom. I should do two replaces or one big one if they are close? They are far apart. 
           I will do two replaces. This call is for the header.) */}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="form-group mb-3">
            <label>Title</label>
            <input type="text" className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
          </div>
          <div className="form-group mb-3">
            <label>Location</label>
            <input type="text" className="form-control" value={location} onChange={(e) => setLocation(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
          </div>
          <div className="form-group mb-3">
            <label>Price</label>
            <input type="text" className="form-control" value={price} onChange={(e) => setPrice(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
          </div>
          <div className="form-group mb-3">
            <label>Tag (e.g. For Sale, Rent, Featured)</label>
            <select className="form-control" value={tag} onChange={(e) => setTag(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
              <option value="For Sale">For Sale</option>
              <option value="For Rent">For Rent</option>
              <option value="Featured">Featured</option>
              <option value="Sold">Sold</option>
            </select>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
          <div className="form-group mb-3">
            <label>Beds</label>
            <input type="number" className="form-control" value={beds} onChange={(e) => setBeds(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
          </div>
          <div className="form-group mb-3">
            <label>Baths</label>
            <input type="number" className="form-control" value={baths} onChange={(e) => setBaths(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
          </div>
          <div className="form-group mb-3">
            <label>Sqft</label>
            <input type="text" className="form-control" value={sqft} onChange={(e) => setSqft(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
          </div>
        </div>

        <div className="form-group mb-3">
          <label>Description</label>
          <textarea className="form-control" rows="4" value={description} onChange={(e) => setDescription(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}></textarea>
        </div>

        <div className="form-group mb-3">
          <label>Property Images</label>
          <div className="d-flex gap-2 flex-column">
             
             {/* Pending Images List */}
             {images.length > 0 && (
               <div className="d-flex flex-wrap gap-2 mb-2">
                 {images.map((img, index) => (
                   <div key={index} style={{ position: 'relative', width: '80px', height: '60px' }}>
                     <img src={img.url} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} />
                     <button
                       type="button"
                       onClick={() => removeImage(index)}
                       style={{
                         position: 'absolute', top: -5, right: -5,
                         background: 'red', color: 'white', border: 'none',
                         borderRadius: '50%', width: '20px', height: '20px',
                         fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                       }}
                     >
                       x
                     </button>
                   </div>
                 ))}
               </div>
             )}

             {/* Add URL */}
             <div className="d-flex gap-2">
               <input 
                 type="text" 
                 className="form-control" 
                 placeholder="Add Image URL" 
                 value={currentUrl} 
                 onChange={(e) => setCurrentUrl(e.target.value)} 
                 style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} 
               />
               <button type="button" onClick={handleAddImageUrl} className="btn btn-secondary btn-sm">Add URL</button>
             </div>
             
             <div className="text-center text-muted">- OR -</div>
             
             {/* Upload Files */}
             <input 
               type="file" 
               multiple
               className="form-control" 
               onChange={handleAddImageFile} 
               style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} 
             />
          </div>
          <small className="text-muted">Upload multiple photos or add URLs. The first image will be the main thumbnail.</small>
        </div>

        <div className="d-flex gap-2" style={{ marginTop: '1rem' }}>
          <button disabled={loading} className="btn btn-primary" type="submit">
            {loading ? (propertyToEdit ? 'Updating...' : 'Uploading...') : (propertyToEdit ? 'Update Property' : 'Add Property')}
          </button>
          
          {propertyToEdit && (
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PropertyForm;
